import { memo, useMemo } from "react";
import Accordion from "../../../components/Accordion";
import TableResponsive from "../../../components/TableResponsive";
import Checkbox from "../../../components/Checkbox";
import SpoilerLink from "../../../components/SpoilerLink";
import IcLixo from "../../../components/Svg/IcLixo";
import Button from "../../../components/Button";
import { IntegerNumber, Currency, InputText } from "../../../components/Input";
import Slct from "../../../components/Slct";
import useFormat from "../../../hooks/utils/useFormat";
import style from "./style.module.scss";
import printRules from "../../../components/PrintRules/style.module.scss";

// Base (qtd por formando × valor unitário × formandos esperados no evento) — já embutida no
// preço do evento/pacote, igual a qualquer outro recurso. As negociações individuais (extras)
// não entram aqui, só no financeiro específico do formando.
export function eventInvitesTotal(event) {
  return (
    (event?.qtd_invitations || 0) * (event?.value_invitation || 0) * (event?.number_trainees || 0) +
    (event?.qtd_tickets || 0) * (event?.value_ticket || 0) * (event?.number_trainees || 0)
  );
}

// Pure row-builder: takes one resource + everything it needs to render/edit itself and returns the exact
// TableResponsive row-descriptor shape the page relied on before this file was extracted.
function buildResourceRow({
  resource,
  resourceIndex,
  eventIndex,
  resourceOptions,
  providerOptions,
  formatReal,
  onUpdate,
  onRemove,
}) {
  return {
    show_spoiler: !!resource?.description,
    key: `resource-${resource?.id ?? `new-${resourceIndex}`}`,
    data: [
      {
        element: (
          <Checkbox
            checked={resource.show}
            onChange={__ev => {
              onUpdate(eventIndex, resourceIndex, { show: __ev.target.checked });
            }}
          />
        ),
        className: printRules.hide_on_print,
      },
      {
        element: (
          <Slct
            required={true}
            placeholder="..."
            value={resource.resource_id}
            onChange={__ev => {
              onUpdate(eventIndex, resourceIndex, {
                resource_id: __ev.value,
                provider_id: null,
              });
            }}
            options={resourceOptions}
          />
        ),
        className: style.padding_left_on_print,
      },
      {
        element: (
          <Slct
            placeholder="..."
            value={resource.provider_id}
            onChange={__ev => {
              onUpdate(eventIndex, resourceIndex, { provider_id: __ev.value });
            }}
            options={providerOptions || []}
          />
        ),
      },
      {
        element: (
          <IntegerNumber
            value={resource.qtd}
            valueChange={__ev => {
              onUpdate(eventIndex, resourceIndex, { qtd: parseInt(__ev) });
            }}
            maxlength="4"
            style={{ width: "75px" }}
            allowNegative={false}
          />
        ),
      },
      {
        element: (
          <Currency
            value={resource.single_value}
            valueChange={__ev => {
              onUpdate(eventIndex, resourceIndex, { single_value: __ev });
            }}
            style={{ width: "140px" }}
            allowNegative={false}
          />
        ),
      },
      {
        element: <span>{formatReal((resource.qtd || 0) * (resource.single_value || 0))}</span>,
      },
      {
        element: <SpoilerLink text="" bold={!!resource.description} />,
        className: printRules.hide_on_print,
        id: `description-${eventIndex}-${resourceIndex}`,
        spoiler: (
          <InputText
            placeholder="Descrição"
            value={resource.description}
            onChange={__ev => {
              onUpdate(eventIndex, resourceIndex, { description: __ev.target.value });
            }}
          />
        ),
      },
      {
        element: (
          <button
            className="simple-ico"
            onClick={() => {
              onRemove(eventIndex, resourceIndex);
            }}
          >
            <IcLixo />
          </button>
        ),
      },
    ],
  };
}

// One Accordion.Item per event, wrapped in React.memo so editing a resource in one event no longer
// re-renders every other event's accordion item / table.
function EventAccordionItem({
  event,
  eventIndex,
  resourceOptions,
  providerOptionsByResource,
  validateCustomComponents,
  onUpdateResource,
  onRemoveResource,
  onAddResource,
  onUpdateInvite,
  onLoadTemplate,
}) {
  const { formatReal } = useFormat();

  const resourcesTotal = useMemo(
    () =>
      event?.resources?.reduce(
        (__acc, __resource) => __acc + (__resource.single_value || 0) * (__resource.qtd || 0),
        0,
      ) || 0,
    [event.resources],
  );

  const invitesTotal = useMemo(() => eventInvitesTotal(event), [event]);

  const valueTotal = resourcesTotal + invitesTotal;

  const notAddedResources = useMemo(
    () =>
      resourceOptions?.filter(__resource => {
        const _event_selected = event?.resources?.find(
          __selected_resource => __selected_resource.resource_id === __resource.value,
        );
        return !_event_selected;
      }) || [],
    [resourceOptions, event.resources],
  );

  const tableBody = useMemo(
    () =>
      event?.resources?.map((__resource, __resource_i) =>
        buildResourceRow({
          resource: __resource,
          resourceIndex: __resource_i,
          eventIndex,
          resourceOptions,
          providerOptions: providerOptionsByResource.get(__resource.resource_id) || [],
          formatReal,
          onUpdate: onUpdateResource,
          onRemove: onRemoveResource,
        }),
      ) || [],
    [
      event.resources,
      eventIndex,
      resourceOptions,
      providerOptionsByResource,
      formatReal,
      onUpdateResource,
      onRemoveResource,
    ],
  );

  return (
    <Accordion.Item
      eventKey={`${eventIndex + 1}`}
      className={`${eventIndex > 0 ? printRules.avoid_break_on_print : ""}`}
      data-new-component={true}
      data-has-error={event?.resources?.some(__resource => !__resource?.resource_id) && !!validateCustomComponents}
    >
      <Accordion.Header>
        <span>
          {event.label}
          <p className="small-label mb-0">
            {" "}
            <b>{event.number_trainees}</b> Formandos
          </p>
        </span>
        <Accordion.Extras>
          <span>{formatReal(valueTotal)}</span>
          {event.number_trainees ? (
            <span>{`${formatReal(valueTotal / event.number_trainees)} / formando`}</span>
          ) : (
            <></>
          )}
        </Accordion.Extras>
      </Accordion.Header>
      <Accordion.Body>
        <Button
          className={`mb-3 ${printRules.hide_on_print}`}
          color="primary_ghost"
          size="sm"
          onClick={() => {
            onLoadTemplate(event.category_id, event.value);
          }}
        >
          Carregar Modelo de Orçamento
        </Button>
        <p className="small-label mb-2">
          <b>Base de Convites e Ingressos por Formando</b> neste evento — já está inclusa no total acima e no
          financeiro do formando (rateada igual entre todos, junto com os recursos). Negociações individuais que
          fugirem dessa base (a mais ou a menos) são tratadas à parte, na renegociação de cada formando.
        </p>
        <TableResponsive
          table={{
            head: [
              { data: "Base de Convites por Formando" },
              { data: "Valor Unitário (Convite)" },
              { data: "Base de Ingressos por Formando" },
              { data: "Valor Unitário (Ingresso)" },
            ],
            body: [
              {
                data: [
                  {
                    element: (
                      <IntegerNumber
                        value={event.qtd_invitations}
                        valueChange={__ev => onUpdateInvite(eventIndex, { qtd_invitations: parseInt(__ev) || null })}
                        allowNegative={false}
                      />
                    ),
                  },
                  {
                    element: (
                      <Currency
                        value={event.value_invitation}
                        valueChange={__ev => onUpdateInvite(eventIndex, { value_invitation: __ev })}
                        allowNegative={false}
                      />
                    ),
                  },
                  {
                    element: (
                      <IntegerNumber
                        value={event.qtd_tickets}
                        valueChange={__ev => onUpdateInvite(eventIndex, { qtd_tickets: parseInt(__ev) || null })}
                        allowNegative={false}
                      />
                    ),
                  },
                  {
                    element: (
                      <Currency
                        value={event.value_ticket}
                        valueChange={__ev => onUpdateInvite(eventIndex, { value_ticket: __ev })}
                        allowNegative={false}
                      />
                    ),
                  },
                ],
              },
            ],
          }}
        />
        <div className="divisor mt-3 mb-3"></div>
        <TableResponsive
          emptyMsg="Nenhum recurso cadastrado neste evento."
          table={{
            head: [
              { data: "Exibir", className: printRules.hide_on_print },
              { data: "Recurso", className: style.padding_left_on_print },
              { data: "Fornecedor" },
              { data: "Quantidade" },
              { data: "Valor Unitário" },
              { data: "Total" },
              { data: "Espec." },
              { data: "", className: printRules.hide_on_print },
            ],
            body: tableBody,
          }}
        />
        <p className={`m-0 mt-2 op-5 ${printRules.hide_not_print}`}>
          <span className="fw-bold">Recursos não adicionados: </span>
          {notAddedResources.map((__resource, __resource_i) => (
            <>
              {__resource_i > 0 ? ", " : ""}
              {__resource.label}
            </>
          ))}
        </p>

        {/*  */}
        <Button
          size="sm"
          className={printRules.hide_on_print}
          onClick={() => {
            onAddResource(eventIndex, {
              show: true,
              resource_id: null,
              provider_id: null,
              qtd: null,
              single_value: null,
              description: "",
            });
          }}
        >
          Adicionar Recurso
        </Button>
      </Accordion.Body>
    </Accordion.Item>
  );
}

export default memo(EventAccordionItem);
